/**
 * Playground — Test AI agents in real-time
 */
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import StatusChip from '@/components/ui/StatusChip';
import adminApi from '@/services/admin.api';
import api from '@/services/api.service';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { sanitizeAssistantText } from '@/utils/assistant-text.util';
import { isPermissionLockTagged } from '@/utils/permission-lock.util';
import {
    DeleteOutline,
    Person,
    Refresh as RefreshIcon,
    Save as SaveIcon,
    Send as SendIcon,
    SmartToy,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Skeleton,
    TextField,
    Typography,
    alpha,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface Agent {
  _id: string;
  name: string;
  description?: string;
  systemPrompt: string;
  model?: string;
  topics?: string[];
  isActive?: boolean;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const PLAYGROUND_CHAT_TIMEOUT_MS = 180000;

// ── Message bubble ─────────────────────────────────────────────────────────
const MessageBubble: React.FC<{ message: Message }> = ({ message }) => {
  const isUser = message.role === 'user';
  const time = new Date(message.timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-end',
        gap: 1,
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isUser ? '#4f46e5' : '#f1f5f9',
          '& svg': { fontSize: 15, color: isUser ? '#fff' : '#64748b' },
        }}
      >
        {isUser ? <Person /> : <SmartToy />}
      </Box>

      {/* Bubble */}
      <Paper
        elevation={0}
        sx={{
          maxWidth: '76%',
          px: 2,
          py: 1.25,
          bgcolor: isUser ? '#4f46e5' : '#fff',
          color: isUser ? '#fff' : 'text.primary',
          borderRadius: isUser ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
          border: isUser ? 'none' : '1px solid #e2e8f0',
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}
        >
          {isUser ? message.content : sanitizeAssistantText(message.content)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            textAlign: 'right',
            opacity: 0.6,
            fontSize: '0.6875rem',
          }}
        >
          {time}
        </Typography>
      </Paper>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const Playground: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAgents();
  }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (selectedAgent) {
      adminApi
        .getPlaygroundPrompt(selectedAgent._id)
        .then((res) => {
          if (res?.systemPrompt) setEditedPrompt(res.systemPrompt);
          else setEditedPrompt(selectedAgent.systemPrompt || '');
        })
        .catch(() => setEditedPrompt(selectedAgent.systemPrompt || ''));
    } else if (agents.length === 0) {
      setEditedPrompt('');
    }
  }, [selectedAgent, agents.length]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError('');
      // suppressPermissionLock: agents is a cross-module dependency here —
      // a playground-only user has no agents grant, but the page still works
      // in ad-hoc mode (agents.length === 0). Silently treat a permission
      // error the same as "no agents" rather than showing a lock popup.
      const response = await adminApi.getAgents(undefined, { suppressPermissionLock: true });
      setAgents(response.data || []);
      if (response.data?.length > 0 && !selectedAgent) {
        setSelectedAgent(response.data[0]);
      }
    } catch (err: any) {
      // Permission-denied: playground module does not imply agents access.
      // Show no error — the page works in ad-hoc mode without an agent list.
      if (!isPermissionLockTagged(err)) {
        setError(err.message || 'Failed to fetch agents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrompt = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload: any = {
        agentName: selectedAgent ? selectedAgent.name : 'Ad-hoc Test Agent',
        systemPrompt: editedPrompt,
      };
      if (selectedAgent) payload.agentId = selectedAgent._id;
      await adminApi.savePlaygroundPrompt(payload);
      setSuccess('Temporary prompt saved! Expires in 2 hours.');
      if (selectedAgent) setSelectedAgent({ ...selectedAgent, systemPrompt: editedPrompt });
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.message || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    if (!selectedAgent && agents.length > 0) return;

    const targetAgentId = selectedAgent ? selectedAgent._id : 'no-agent';
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setError('');
    setSendingMsg(true);

    // The assistant bubble is created lazily on the first token so a reply
    // that never streams anything (a hard failure before any text) doesn't
    // leave a stray empty bubble behind.
    const assistantId = `assistant-${Date.now()}`;
    let assistantStarted = false;

    const timeoutController = new AbortController();
    const timeoutTimer = setTimeout(() => timeoutController.abort(), PLAYGROUND_CHAT_TIMEOUT_MS);

    try {
      await api.streamPost(
        '/admin/playground/chat',
        {
          message: userMsg.content,
          agentId: targetAgentId,
          systemPrompt: editedPrompt,
          conversationHistory: messages.map((m) => ({ role: m.role, content: m.content })),
        },
        (frame) => {
          if (frame.type === 'token') {
            const delta = typeof frame['content'] === 'string' ? (frame['content'] as string) : '';
            if (!delta) return;
            if (!assistantStarted) {
              assistantStarted = true;
              setMessages((prev) => [
                ...prev,
                {
                  id: assistantId,
                  role: 'assistant',
                  content: delta,
                  timestamp: new Date().toISOString(),
                },
              ]);
            } else {
              setMessages((prev) =>
                prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m))
              );
            }
          } else if (frame.type === 'reset') {
            // The server discarded a partial answer (e.g. RAG streamed a few
            // tokens then failed) and is about to send a full replacement —
            // clear the bubble in place rather than appending onto stale text.
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: '' } : m))
            );
          } else if (frame.type === 'error') {
            const msg = frame['message'];
            throw new Error(typeof msg === 'string' ? msg : 'Failed to get response');
          }
          // 'done' carries the same text already streamed via 'token' frames — nothing more to render.
        },
        { signal: timeoutController.signal }
      );

      if (!assistantStarted) {
        throw new Error('Failed to get response');
      }
    } catch (err: any) {
      // Always drop a partially-streamed bubble, whichever way this failed.
      if (assistantStarted) {
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      }

      // A plan/quota or RBAC denial belongs in the global lock dialog, not an
      // inline chat bubble — same pattern as Agents.tsx / ChatLogs.tsx.
      // api.service's streamPost tags these even though it bypasses the axios
      // interceptors.
      if (showPlanLockIfTagged(err) || isPermissionLockTagged(err)) {
        return;
      }

      const isTimeoutError = err?.name === 'AbortError';
      const errorMessage = isTimeoutError
        ? 'Request timed out after 180s. Please try again.'
        : err.message || 'Failed to send message';

      setError(errorMessage);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: `⚠️ ${errorMessage}`,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      clearTimeout(timeoutTimer);
      setSendingMsg(false);
    }
  };

  const canSend =
    Boolean(inputMessage.trim()) && (Boolean(selectedAgent) || agents.length === 0) && !sendingMsg;

  return (
    <Box>
      <PageHeader
        title="Playground"
        subtitle="Test your AI agents with temporary prompts"
        actions={
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={fetchAgents}
            disabled={loading}
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* ── Left: Agent selector + prompt editor ─────────────────────── */}
        <Grid item xs={12} md={4}>
          {/* Agent list */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Select Agent
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1 }}>
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton height={14} />
                        <Skeleton height={12} width="70%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : agents.length === 0 ? (
                <EmptyState
                  icon={<SmartToy />}
                  title="No agents"
                  description="Write a prompt directly on the right to test without an agent."
                />
              ) : (
                <List dense disablePadding>
                  {agents.map((agent) => {
                    const isSelected = selectedAgent?._id === agent._id;
                    return (
                      <ListItemButton
                        key={agent._id}
                        selected={isSelected}
                        onClick={() => setSelectedAgent(agent)}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          '&.Mui-selected': {
                            bgcolor: alpha('#4f46e5', 0.09),
                            '&:hover': { bgcolor: alpha('#4f46e5', 0.12) },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            mr: 1.5,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: isSelected ? '#4f46e5' : '#f1f5f9',
                            '& svg': { fontSize: 16, color: isSelected ? '#fff' : '#64748b' },
                          }}
                        >
                          <SmartToy />
                        </Box>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {agent.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {agent.topics?.join(', ') || agent.model || 'General'}
                            </Typography>
                          }
                        />
                        {agent.isActive !== false && <StatusChip status="active" sx={{ ml: 1 }} />}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Prompt editor */}
          {(selectedAgent || agents.length === 0) && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                  {agents.length === 0 ? 'Ad-Hoc Prompt' : 'Testing Prompt'}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}
                >
                  {selectedAgent
                    ? `Editing: ${selectedAgent.name}`
                    : 'Will not affect live agents.'}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  maxRows={16}
                  label="System Prompt"
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                  placeholder="Enter system prompt…"
                  sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleUpdatePrompt}
                  disabled={
                    saving ||
                    (selectedAgent !== null && editedPrompt === selectedAgent.systemPrompt)
                  }
                >
                  {saving ? (
                    <CircularProgress size={18} sx={{ color: 'inherit' }} />
                  ) : (
                    'Save to Playground'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* ── Right: Chat interface ─────────────────────────────────────── */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              height: { xs: 480, md: 'calc(100vh - 200px)' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Chat header */}
            <CardContent sx={{ pb: '12px !important', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6">
                    {selectedAgent
                      ? `Chat with ${selectedAgent.name}`
                      : agents.length === 0
                        ? 'Ad-Hoc Chat'
                        : 'Select an agent to start'}
                  </Typography>
                  {selectedAgent?.description && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedAgent.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {selectedAgent?.model && (
                    <Chip label={selectedAgent.model} size="small" variant="outlined" />
                  )}
                  {messages.length > 0 && (
                    <IconButton
                      size="small"
                      onClick={() => setMessages([])}
                      aria-label="Clear chat"
                      sx={{ color: 'text.secondary' }}
                    >
                      <DeleteOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </CardContent>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                bgcolor: '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {messages.length === 0 ? (
                <EmptyState
                  icon={<SmartToy />}
                  title={
                    selectedAgent || agents.length === 0
                      ? 'Start a conversation'
                      : 'Select an agent first'
                  }
                  description={
                    selectedAgent || agents.length === 0
                      ? 'Type a message below to test the agent`s responses'
                      : 'Choose an agent from the left panel'
                  }
                />
              ) : (
                messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
              )}
              {sendingMsg && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SmartToy sx={{ fontSize: 15, color: '#64748b' }} />
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 2,
                      py: 1.25,
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px 16px 16px 16px',
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#94a3b8',
                            animation: 'pulse 1.4s ease-in-out infinite',
                            animationDelay: `${i * 0.2}s`,
                            '@keyframes pulse': {
                              '0%,80%,100%': { transform: 'scale(0.6)', opacity: 0.4 },
                              '40%': { transform: 'scale(1)', opacity: 1 },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>

            <Divider />

            {/* Input */}
            <CardContent sx={{ pt: 1.5, pb: '12px !important' }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={5}
                  placeholder={
                    selectedAgent || agents.length === 0
                      ? 'Type a message… (Shift+Enter for new line)'
                      : 'Select an agent first'
                  }
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (canSend) handleSendMessage();
                    }
                  }}
                  disabled={!selectedAgent && agents.length > 0}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                />
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!canSend}
                  aria-label="Send message"
                  sx={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    bgcolor: canSend ? '#4f46e5' : '#e2e8f0',
                    color: canSend ? '#fff' : '#94a3b8',
                    borderRadius: 2,
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: canSend ? '#4338ca' : '#e2e8f0' },
                    '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Playground;
